import React, { useState } from "react";
import styled from "styled-components";
import { layout, variant } from "styled-system";
import PropTypes from "prop-types";

import { getHTMLDecodedText } from "util/chatUtil";
import Icon from "konnekt/Icon";

const StyledMessageContainer = styled.div`
  display: flex;
  align-items: center;
  min-width: 150px;
  position: relative;
  ${layout}
`;

const StyledAudioTimeContainer = styled.div`
  position: absolute;
  right: 0;
  transform: translateY(100%);
  font-size: 14px;
  line-height: 135.69%;
  color: #222222;

  ${variant({
    variants: {
      light: {
        color: "rgba(255, 255, 255, 0.7)",
      },
    },
  })}
`;

const StyledCanvas = styled.canvas`
  content: "";
  width: calc(100% - 47px);
  height: 3px;
  background-color: #c2c2c2;

  ${variant({
    variants: {
      light: {
        backgroundColor: "rgba(255, 255, 255, 0.4)",
      },
    },
  })}
`;

const IconBox = styled.div`
  ${variant({
    variants: {
      light: {
        backgroundColor: "#F1F0F5",
        marginRight: "12px",
        borderRadius: "50%",
        padding: "8px",
      },
    },
  })}
`;

const AudioMessage = ({ mediaURL, previewOnly, ...rest }) => {
  const [duration, setDuration] = useState("00:00");
  const [isPlaying, setPlayingStatus] = useState(false);
  const audioRef = React.createRef();
  const canvasRef = React.createRef();

  //when audio start playing
  const onPlay = () => {
    audioRef.current.play();
    setPlayingStatus(true);
  };

  //changing seconds into MM::SS format
  const changeTimeFormat = (time) => {
    var minutes = `0${  Math.floor(time / 60)}`;
    var seconds = `0${  time - minutes * 60}`;
    return `${minutes.substr(-2)  }:${  seconds.substr(-2)}`;
  };

  // seekbar time
  const updateCurrentTime = () => {
    let canvas = canvasRef.current;
    let ctx = canvas.getContext("2d");
    const currentTime = audioRef.current.currentTime | 0;
    const currentDuration = audioRef.current.duration - 1;
    const percentage = currentTime / currentDuration;
    const progress = canvas.width * percentage;

    ctx.fillStyle = rest.variant === "light" ? "white" : "green";
    ctx.fillRect(0, 0, progress, canvas.height);
    setDuration(changeTimeFormat(currentTime));
  };

  const onEnd = () => {
    let canvas = canvasRef.current;
    let ctx = canvas.getContext("2d");
    ctx.fillStyle = "silver";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setPlayingStatus(false);
  };

  //song paused
  const onPause = () => {
    audioRef.current.pause();
    setPlayingStatus(false);
  };

  // In preview only mode -> Do not allow to click on play button
  const onPlayClick = () => {
    if (!previewOnly) {
      isPlaying ? onPause() : onPlay();
    }
  };

  return (
    <div>
      <StyledMessageContainer {...rest}>
        <audio
          ref={audioRef}
          src={getHTMLDecodedText(mediaURL)}
          preload="auto"
          onTimeUpdate={() => updateCurrentTime()}
          onEnded={() => onEnd()}
          onLoadedMetadata={() =>
            setDuration(changeTimeFormat(Math.floor(audioRef.current.duration)))
          }
        />
        <IconBox {...rest}>
          <Icon
            iconSize={rest.variant === "light" ? "xs" : "normal"}
            onClick={onPlayClick}
            iconName={isPlaying ? "pause" : "play"}
          />
        </IconBox>
        <StyledCanvas {...rest} ref={canvasRef} />
        <StyledAudioTimeContainer {...rest}>
          {duration}
        </StyledAudioTimeContainer>
      </StyledMessageContainer>
    </div>
  );
};

AudioMessage.propTypes = {
  mediaUrl: PropTypes.string.isRequired,
  previewOnly: PropTypes.bool,
};

AudioMessage.defaultProps = {
  previewOnly: false,
};

export default AudioMessage;
